import React from 'react';
import { Button } from '@/components/ui/button';
import * as Sentry from '@sentry/react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, eventId: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    
    // Capture error with Sentry and get event ID
    Sentry.withScope((scope) => {
      scope.setExtras(errorInfo);
      const eventId = Sentry.captureException(error);
      this.setState({
        error,
        errorInfo,
        eventId
      });
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, eventId: null });
    window.location.href = '/';
  };

  handleReportFeedback = () => {
    if (this.state.eventId) {
      Sentry.showReportDialog({ eventId: this.state.eventId });
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-indigo-950 via-purple-900 to-indigo-950 flex items-center justify-center p-4">
          <div className="max-w-lg w-full bg-white/10 backdrop-blur-lg rounded-lg p-8 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-3xl font-bold text-white mb-4">
              Oops! Something went wrong
            </h1>
            <p className="text-gray-300 mb-6">
              We encountered an unexpected error. Our team has been notified and we're working on a fix.
            </p>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="bg-red-900/20 border border-red-500/50 rounded p-4 mb-6 text-left">
                <p className="text-red-300 text-sm font-mono break-all">
                  {this.state.error.toString()}
                </p>
              </div>
            )}
            
            <div className="space-y-3">
              <Button 
                onClick={this.handleReset}
                className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
              >
                Return to Home
              </Button>
              <Button 
                onClick={() => window.location.reload()}
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/10"
              >
                Reload Page
              </Button>
              {this.state.eventId && (
                <Button 
                  onClick={this.handleReportFeedback}
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/10"
                >
                  Report Feedback
                </Button>
              )}
            </div>
            
            <p className="text-xs text-gray-400 mt-6">
              Error ID: {this.state.eventId || 'N/A'}
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
